import { parseReviewCsv } from './reviewPreparation';

export const PAIRED_REPORT_PREFIX = 'CompTIA paired report · ';
export const PAIRED_SOURCE_PREFIX = 'CompTIA paired reports · ';
export type CourseItem = { kind: string; code: string; title: string; header: string; key: string };
export type CourseReport = { items: CourseItem[]; rows: string[][]; repairedHeader: boolean };
export type PairedItem = CourseItem & { result: string; seconds: number | null; timeColumnPresent: boolean };
export type ReportCounts = {
  activities: number; reportedResults: number; completedItems: number; assessmentScores: number;
  assessmentsBelow80: number; labResults: number; timedItems: number; zeroTimeItems: number; recordedSeconds: number;
};
export type ReportPair = {
  reportKind?: 'progress' | 'time';
  items: PairedItem[]; totals: ReportCounts; modules: Array<{ code: string; counts: ReportCounts }>;
  matchedColumns: number; progressOnlyColumns: number; timeOnlyColumns: number; timeOnlyRecordedItems: number;
};
export type PairMetadata = {
  course: string; block: string; startDate: string; endDate: string;
  history: 'block' | 'progress_cumulative' | 'cumulative'; scope: 'review' | 'planning';
};
export type PairedEvidence = { course?: string; reportKind?: 'progress' | 'time'; source: string; date: string; scope: 'review' | 'planning'; records: Array<{ title: string; value: string }> };
export type ReviewPeriod = { start_date?: string; end_date?: string; title?: string };
export type ReportBlock = { id: number; title: string; start_date: string; end_date: string };

const itemHeader = /^(Fact Sheet|Video Lesson|Assessment|Lab|Activity)\s*-\s*((?:[A-Z]|\d+)(?:\.\d+)+)\s+(.+)$/i;
const normalize = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();
const codeKey = (value: string) => value.split('.').map(part => /^\d+$/.test(part) ? String(Number(part)) : part.toUpperCase()).join('.');
export class ReportPairError extends Error {}
const fail = (message: string): never => { throw new ReportPairError(message); };

export function parseCompTIAReport(text: string): CourseReport {
  let parsed: string[][];
  try { parsed = parseReviewCsv(text.replace(/^\uFEFF/, '')); }
  catch { return fail('Could not read this CSV. Use a CompTIA export under 2 MB with a header and student rows.'); }
  if (normalize(parsed[0][0]) !== 'student') return fail('Expected a CompTIA export with Student as its first column.');
  let headers = parsed[0];
  let repairedHeader = false;
  const widths = new Set(parsed.slice(1).map(row => row.length));
  if (widths.size !== 1) return fail('Student rows have different column counts. Re-export the report; columns cannot be matched safely.');
  const width = parsed[1].length;
  if (headers.length !== width) {
    // Narrow repair for CompTIA's unquoted commas in activity titles. Never pad,
    // truncate, or shift student values. Every row must agree with the repair.
    const repaired = [headers[0]];
    for (const cell of headers.slice(1)) {
      if (itemHeader.test(cell.trim())) repaired.push(cell.trim());
      else if (repaired.length > 1 && cell.trim()) repaired[repaired.length - 1] += `,${cell}`;
      else return fail('The activity header cannot be aligned safely. Re-export the report.');
    }
    if (headers.length <= width || repaired.length !== width) return fail('The activity header does not match the student rows. Re-export the report.');
    headers = repaired;
    repairedHeader = true;
  }
  const identities = new Set<string>();
  const items = headers.slice(1).map(header => {
    const match = header.trim().match(itemHeader);
    if (!match || header.trim().length > 500) return fail('An activity header is unsupported or too long. Use an unmodified CompTIA activity export.');
    const [, kind, code, title] = match;
    const key = `${normalize(kind)}:${codeKey(code)}`;
    if (identities.has(key)) return fail('The report repeats an activity type and code. Re-export one course version so activities can be matched unambiguously.');
    identities.add(key);
    return { kind, code: codeKey(code), title: title.trim(), header: header.trim(), key };
  });
  if (new Set(items.map(item => item.code.split('.')[0])).size > 64) return fail('This export contains too many modules. Export one CompTIA course at a time.');
  return { items, rows: parsed.slice(1), repairedHeader };
}

export function recordedSeconds(value: string): number | null {
  const text = value.trim().toLowerCase();
  if (['', '-', '--', 'n/a'].includes(text)) return null;
  let seconds: number;
  const clock = text.match(/^(\d+):([0-5]\d):([0-5]\d)$/);
  if (clock) seconds = Number(clock[1]) * 3600 + Number(clock[2]) * 60 + Number(clock[3]);
  else {
    // A bare number or two-part clock is ambiguous; do not guess its unit.
    const units = text.match(/^(?:(\d+(?:\.\d+)?)\s*h\s*)?(?:(\d+(?:\.\d+)?)\s*m\s*)?(?:(\d+(?:\.\d+)?)\s*s\s*)?$/);
    if (!units || !units.slice(1).some(v => v !== undefined)) return fail('A time value has an unsupported format. Export durations with h, m, s units or HH:MM:SS; bare numbers are ambiguous.');
    seconds = Number(units[1] || 0) * 3600 + Number(units[2] || 0) * 60 + Number(units[3] || 0);
  }
  if (!Number.isFinite(seconds) || seconds > 366 * 86400) return fail('A recorded duration is outside the supported range. Check the time report.');
  return Math.round(seconds * 1000) / 1000;
}

export function formatRecordedTime(seconds: number | null): string {
  if (seconds === null) return 'Not reported';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round((seconds % 60) * 1000) / 1000;
  return [h ? `${h}h` : '', m ? `${m}m` : '', s || (!h && !m) ? `${s}s` : ''].filter(Boolean).join(' ');
}

function resultValue(raw: string): string {
  const value = normalize(raw);
  if (['', '-', '--', 'n/a'].includes(value)) return '';
  if (/^\d+(?:\.\d+)?\s*%$/.test(value) && Number(value.replace('%', '').trim()) <= 100) return `${Number(value.replace('%', '').trim())}%`;
  if (['complete', 'completed', 'passed', 'incomplete', 'not completed', 'not started', 'in progress', 'failed', 'not attempted', 'ungraded'].includes(value)) return value;
  return fail('A progress value is not a percentage or recognized course status. Check that the progress and time files are in the correct upload fields.');
}

function selectedRow(report: CourseReport, index: number): string[] {
  if (!Number.isInteger(index) || index < 0 || index >= report.rows.length) return fail('Select a student row in each report.');
  const row = report.rows[index];
  const name = normalize(row[0]);
  if (!name || report.rows.filter(candidate => normalize(candidate[0]) === name).length !== 1) return fail('The selected name is blank or duplicated in a report. Resolve the duplicate before pairing; the app will not guess the student.');
  return row;
}

const emptyCounts = (): ReportCounts => ({ activities: 0, reportedResults: 0, completedItems: 0,
  assessmentScores: 0, assessmentsBelow80: 0, labResults: 0, timedItems: 0, zeroTimeItems: 0, recordedSeconds: 0 });
function countItem(counts: ReportCounts, item: PairedItem) {
  counts.activities++;
  if (item.result) counts.reportedResults++;
  const assessment = normalize(item.kind) === 'assessment';
  if (['complete', 'completed'].includes(item.result) || (!assessment && item.result === '100%')) counts.completedItems++;
  if (assessment && item.result.endsWith('%')) {
    counts.assessmentScores++;
    if (Number(item.result.slice(0, -1)) < 80) counts.assessmentsBelow80++;
  }
  if (normalize(item.kind) === 'lab' && item.result) counts.labResults++;
  if (item.seconds !== null) {
    counts.timedItems++;
    if (item.seconds === 0) counts.zeroTimeItems++;
    counts.recordedSeconds = Math.round((counts.recordedSeconds + item.seconds) * 1000) / 1000;
  }
}

export function pairCompTIAReports(progress: CourseReport, progressIndex: number, time: CourseReport, timeIndex: number): ReportPair {
  const progressRow = selectedRow(progress, progressIndex);
  const timeRow = selectedRow(time, timeIndex);
  if (normalize(progressRow[0]) !== normalize(timeRow[0])) return fail('The selected rows have different student names. Select matching rows from the two reports.');
  const times = new Map(time.items.map((item, i) => [item.key, { item, seconds: recordedSeconds(timeRow[i + 1]) }]));
  const progressKeys = new Set(progress.items.map(item => item.key));
  let matchedColumns = 0;
  const totals = emptyCounts();
  const modules = new Map<string, ReportCounts>();
  const items = progress.items.map((item, i) => {
    const matched = times.get(item.key);
    if (matched && normalize(matched.item.title) !== normalize(item.title)) return fail('An activity code has different titles in the two reports. Confirm the course version and re-export matching reports.');
    if (matched) matchedColumns++;
    const paired = { ...item, result: resultValue(progressRow[i + 1]), seconds: matched?.seconds ?? null, timeColumnPresent: !!matched };
    countItem(totals, paired);
    const code = item.code.split('.')[0];
    const module = modules.get(code) || emptyCounts();
    countItem(module, paired); modules.set(code, module);
    return paired;
  });
  if (!matchedColumns) return fail('No matching activities were found. Choose exports from the same CompTIA course and version.');
  return { items, totals, modules: Array.from(modules, ([code, counts]) => ({ code, counts })),
    matchedColumns, progressOnlyColumns: items.length - matchedColumns,
    timeOnlyColumns: time.items.length - matchedColumns,
    timeOnlyRecordedItems: Array.from(times.values()).filter(entry => !progressKeys.has(entry.item.key) && entry.seconds !== null).length };
}

export function validReportDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

// A single export has explicitly unknown values for the missing report, never zero results.
export function singleCompTIAReport(report: CourseReport, index: number, kind: 'progress' | 'time'): ReportPair {
  const row = selectedRow(report, index);
  const totals = emptyCounts();
  const modules = new Map<string, ReportCounts>();
  const items = report.items.map((item, i) => {
    const result = { ...item, result: kind === 'progress' ? resultValue(row[i + 1]) : '',
      seconds: kind === 'time' ? recordedSeconds(row[i + 1]) : null, timeColumnPresent: kind === 'time' };
    countItem(totals, result);
    const code = item.code.split('.')[0];
    const counts = modules.get(code) || emptyCounts();
    countItem(counts, result); modules.set(code, counts);
    return result;
  });
  return { reportKind: kind, items, totals, modules: Array.from(modules, ([code, counts]) => ({ code, counts })),
    matchedColumns: 0, progressOnlyColumns: kind === 'progress' ? items.length : 0,
    timeOnlyColumns: kind === 'time' ? items.length : 0, timeOnlyRecordedItems: kind === 'time' ? totals.timedItems : 0 };
}
export function canUsePairForReview(metadata: PairMetadata, period: ReviewPeriod): boolean {
  return metadata.history === 'block' && validReportDate(metadata.startDate) && validReportDate(metadata.endDate)
    && metadata.startDate <= metadata.endDate && validReportDate(period.start_date || '') && validReportDate(period.end_date || '')
    && metadata.startDate >= period.start_date! && metadata.endDate <= period.end_date!;
}

export function pairedReportEvidence(pair: ReportPair, metadata: PairMetadata, period: ReviewPeriod): PairedEvidence {
  if (!metadata.course.trim() || metadata.course.length > 120 || !metadata.block.trim() || metadata.block.length > 120
    || !validReportDate(metadata.startDate) || !validReportDate(metadata.endDate) || metadata.startDate > metadata.endDate
    || !['block', 'progress_cumulative', 'cumulative'].includes(metadata.history)) return fail('Confirm the course/version, reporting block, and valid report dates before including evidence.');
  const scope = metadata.scope === 'review' && canUsePairForReview(metadata, period) ? 'review' : 'planning';
  const summary = (label: string, value: string) => ({ title: PAIRED_REPORT_PREFIX + label, value });
  if (pair.reportKind) {
    const progress = pair.reportKind === 'progress';
    return { course: metadata.course.trim(), reportKind: pair.reportKind, source: `CompTIA ${progress ? 'progress' : 'time'} report · ${metadata.course.trim()}`,
      date: metadata.endDate, scope: 'planning', records: [
        summary('Reporting period', `${metadata.block}; ${metadata.startDate} through ${metadata.endDate}; ${metadata.course}. ${metadata.history === 'block' ? 'Period-only report.' : 'Cumulative history; not current-block accomplishments.'}`),
        summary('Results', progress ? `${pair.totals.completedItems} completed activities; ${pair.totals.assessmentScores} assessment scores; ${pair.totals.assessmentsBelow80} below 80%. Time report not supplied.`
          : `${formatRecordedTime(pair.totals.recordedSeconds)} recorded across ${pair.totals.timedItems} activities; ${pair.items.length - pair.totals.timedItems} durations unreported. Progress results not supplied; time does not establish completion.`),
        summary('Interpretation', 'Missing results and time are unknown. Recorded time can include idle time and retries. Use the current calendar for future available time and the pacing guide for workload; this report does not determine learning speed or readiness.'),
        ...pair.items.filter(item => item.result || item.seconds !== null).map(item => ({ title: item.header,
          value: progress ? `Reported result: ${item.result}. Recorded time unknown.` : `Recorded time: ${formatRecordedTime(item.seconds)}. Progress unknown.` })),
      ] };
  }
  const history = metadata.history === 'block' ? 'Both exports cover this block only.' : metadata.history === 'progress_cumulative'
    ? 'Progress includes earlier blocks or years; recorded time covers the stated period only. Do not attribute cumulative completions to this period or derive a completion rate from these totals.'
    : 'Both exports include cumulative history. The dates describe the cumulative coverage, not current-block accomplishments.';
  const records = [
    summary('Reporting period', `${metadata.block.trim()}; ${metadata.startDate} through ${metadata.endDate}; course/version: ${metadata.course.trim()}. ${history} Use: ${scope === 'review' ? 'teacher-confirmed review-period evidence' : 'planning only; not current-period achievements'}.`),
    summary('Coverage', `${pair.items.length} progress activities; ${pair.matchedColumns} matching time columns; ${pair.progressOnlyColumns} progress activities without a time column. ${pair.timeOnlyColumns} extra time-report activities (${pair.timeOnlyRecordedItems} with recorded time) are excluded from matched totals and do not establish completed work.`),
    summary('Recorded totals', JSON.stringify(pair.totals)),
    summary('Interpretation', 'Time totals cover only activities present in the progress export and include unfinished items. Recorded time may include idle time or retries and excludes untracked work. Blank time is missing, not zero; zero is a reported value, not proof of no effort. Percentages for readings/videos describe completion; assessment percentages describe scores; lab percentages remain reported lab results. A missing result does not establish missing assigned work. No elapsed learning rate, weekly pace, certification readiness, or mastery is inferred. Counts scan the full selected row; raw activity excerpts may be shortened for the local model. Match lesson-level estimates to equivalent coverage before comparing time.'),
    ...pair.modules.map(module => summary(`Module ${module.code}`, JSON.stringify(module.counts))),
    ...pair.items.filter(item => item.result || item.seconds !== null).map(item => ({
      title: item.header,
      value: `Reported ${normalize(item.kind) === 'assessment' ? 'assessment result' : normalize(item.kind) === 'lab' ? 'lab result' : 'completion/status'}: ${item.result || 'not reported'}; recorded time: ${formatRecordedTime(item.seconds)}${item.timeColumnPresent ? '' : ' (no matching time column)'}.`,
    })),
  ];
  // Names, row indices, filenames, and all unselected rows are intentionally absent.
  return { course: metadata.course.trim(), source: PAIRED_SOURCE_PREFIX + metadata.course.trim(), date: metadata.endDate, scope, records };
}
